import officeapi from "@/api/officeapi"
import { apiDate } from "@/api/apicommon"
import i18n from '@/i18n';
import { TaskBoxesResponse } from "@/apiGenerate/medserver";

export function getTaskBoxTitle(taskBox: TaskBoxesResponse, taskBoxList: TaskBoxesResponse[], pwrAppUser: any) {
  if (taskBox.taskBoxChannelType === 'None') return taskBox.taskBoxTitle;
  if (taskBox.taskBoxChannelType === 'Results') return "Résultats";
  const fullTaskBox = taskBoxList.find(tb => tb.taskBoxId == taskBox.taskBoxId)
  const pwrParticipant = fullTaskBox?.pwrParticipants ? fullTaskBox.pwrParticipants.find(
      participant => participant.personWithRoleId !== pwrAppUser.personWithRoleId
  ) : undefined;
  return pwrParticipant 
    ? `${pwrParticipant.firstName} ${pwrParticipant.lastName.toUpperCase()}` 
    : "";
}

function isValidKey<T extends Record<string, unknown>>(
  obj: T,
  key: PropertyKey
): key is keyof T {
  return key in obj;
}

export function getTaskBoxIcon(taskBox: TaskBoxesResponse) {
  if (taskBox.taskBoxChannelType === 'Results') return 'documents';
  if (isValidKey(taskModuleIconMap, taskBox.taskBoxModuleType)) {
    return taskModuleIconMap[taskBox.taskBoxModuleType];
  } else {
    return '';
  }
}

export const taskPriorityTypes = [
  "DuringShift",
  "EndOfShift",
  "NextDays",
  "None"
]

export function getTaskBoxIndex(taskBox: TaskBoxesResponse) {
  return taskBoxModules.indexOf(taskBox.taskBoxModuleType)
}

const taskBoxModules = [
  "Patient",
  "Accounting",
  "Stock",
  "Staff",
  "Learning",
  "Teaching",
  "Research",
  "None",
]

export const taskModuleTerms = {
  "Patient" : "Patient",
  "Accounting" : "Comptabilité",
  "Stock" : "Stock",
  "Staff" : "Ress.humaines",
  "Learning" : "Formation",
  "Teaching" : "Education",
  "Research" : "Recherche",
  "None" : "Autre",
}

export const taskModuleIconMap = {
  "Patient" : "users",
  "Accounting" : "accounting",
  "Stock" : "stock",
  "Staff" : "humanRessources",
  "Learning" : "cme",
  "Teaching" : "teaching",
  "Research" : "lab",
  "None" : "clipboard",
}

const PRIORITY_ORDER = {
  "DuringShift": 1,
  "EndOfShift": 2,
  "NextDays": 3,
  "None": 4,
};

function sortTasksByPriority(tasks: any) {
  return [...tasks].sort(
    (a, b) => {
      const priorityTypeA = a.taskPriorityType;
      let priorityA = 5;
      if (isValidKey(PRIORITY_ORDER, priorityTypeA)) {
        priorityA = PRIORITY_ORDER[priorityTypeA]
      }
      const priorityTypeB = b.taskPriorityType;
      let priorityB = 5;
      if (isValidKey(PRIORITY_ORDER, priorityTypeB)) {
        priorityB = PRIORITY_ORDER[priorityTypeB]
      }
      return priorityA - priorityB;
    }
  );
}

export async function getPatientTasksForSticky(personWithRoleId: string) {
	if (!personWithRoleId) return [];

  //Backend
	const allTasks: (Record<string, any> & {created: Date | string})[] = await officeapi.getTasksAndNotesForPatient(personWithRoleId)
  if (!allTasks) {console.log("Error: allTasks is null"); return [];}

	//Sort
	allTasks.sort((a, b) => apiDate(a.created).getTime() - apiDate(b.created).getTime() || b.taskId - a.taskId)
	allTasks.forEach((task: any) => {
			task.taskNotes.sort((a: any, b: any) => apiDate(a.created).getTime() - apiDate(b.created).getTime())
	})

  //Fill tasks into PriorityGroups
	const healthcareTasks: any[] = []
	const missionTasks: any[] =  []
	const settledTasks: any[] = []
	const futureTasks: any[] =  []

  const currDate = new Date()
  allTasks.forEach((task: any) => {
    if (task.taskStatus === "Settled") {
      settledTasks.push(task)
    } else if (task.showDateTime && apiDate(task.showDateTime) > currDate) {
      futureTasks.push(task)
    } else if (['Administrative', 'Healthcare'].includes(task.patientNoteType)) {
      healthcareTasks.push(task)
    } else {
      missionTasks.push(task)
    }

  })

  //Create NoteGroups
  const allGroups = [
    createNoteGroup(NoteGroup.MISSIONS, sortTasksByPriority(missionTasks)),
    createNoteGroup(NoteGroup.NOTES, sortTasksByPriority(healthcareTasks)),
    createNoteGroup(NoteGroup.FUTURE, sortTasksByPriority(futureTasks)),
    createNoteGroup(NoteGroup.VALIDATED, sortTasksByPriority(settledTasks)),
  ];

  //Return
  return allGroups
}

export enum NoteGroup {
  MISSIONS,
  NOTES,
  FUTURE,
  VALIDATED,
}

export function getNoteGroupLabel(key: NoteGroup): string {
  return i18n.global.t(`task.noteGroup.${NoteGroup[key].toLowerCase()}`);
}

function createNoteGroup(keyword: NoteGroup, taskArray: any[]) {
  return {
    "keyword": keyword,
    "tasks": taskArray,
  };
}



//EMAIL, PHONE, ADDRESS, CREDIT CARDS
export function sortByIsPreferredThenCreationDate(array: (any & { isPreferred: boolean, created: string })[]) {
  //For sorting Emails, Phones, Addresses, Languages, CreditCards
	return array.sort((a, b) => {
		if (a.isPreferred && !b.isPreferred) return -1;
		if (!a.isPreferred && b.isPreferred) return 1;
		return new Date(a.created).getTime() - new Date(b.created).getTime();
	})
}

export function getDefaultPersonItem(array: (any & { isPreferred: boolean, created: string })[]) {
  //For getting the preferred or first created of: Emails, Phones, Addresses, Languages, CreditCards
  array = sortByIsPreferredThenCreationDate(array)
  return array[0] || null
}




//ADMINISTRATIVE GENDER
export const GENDER_LIST = [
  "Male",
  "Female",
  "NonBinary",
]
export const GENDER_TERMS = {
  "Male": "Masculin",
  "Female": "Féminin",
  "NonBinary" : "Autre",
}

//=BIOLOGICAL SEX in regard to inner organs at birth
export const BIOLOGICAL_SEX_LIST = [
  "Male",
  "Female",
  "Intersex",
  "Indeterminate"
]

export const BIOLOGICAL_SEX_TERMS = {
  "Male": "Masculin",
  "Female": "Féminin",
  "Intersex" : "Intersex",
  "Indeterminate": "Indéterminé",
}

export function getGenderIcon(pwrPatient: any) {
  if (!pwrPatient) return "";
  const { gender, biologicalSex } = pwrPatient;
  if (gender !== biologicalSex) return "⚥";
  if (gender === "Male") return "♂";
  if (gender === "Female") return "♀";
  return "";
}
